import { useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  query, 
  orderBy, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebaseClient';
import { toast } from 'sonner';

export const useClasses = (filters = {}) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let q = collection(db, 'class_management');

    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.clientId) {
      q = query(q, where('user_id', '==', filters.clientId));
    }

    // Order by created date (newest first)
    q = query(q, orderBy('created_at', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const classesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          
          // Safe date parsing function
          const parseDate = (dateValue) => {
            if (!dateValue) return null;
            
            try {
              // If it's already a Firestore timestamp, convert it
              if (dateValue?.toDate) {
                return dateValue.toDate();
              }
              
              // Try to parse as a regular date
              const date = new Date(dateValue);
              
              // Check if the date is valid
              if (isNaN(date.getTime())) {
                console.warn('Invalid date value:', dateValue);
                return null;
              }
              
              return date;
            } catch (error) {
              console.warn('Error parsing date:', dateValue, error);
              return null;
            }
          };
          
          return {
            id: doc.id,
            ...data,
            created_at: parseDate(data.created_at) || new Date(),
            start_date: parseDate(data.start_date),
            end_date: parseDate(data.end_date),
          };
        });
        setClasses(classesData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error fetching classes:', error);
        setError(error);
        setLoading(false);
        toast.error('Failed to load classes');
      }
    );

    return () => unsubscribe();
  }, [filters.status, filters.clientId]);

  const createClass = async (classData) => {
    try {
      const docRef = await addDoc(collection(db, 'class_management'), {
        ...classData,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        status: 'pending',
      });
      toast.success('Class created successfully');
      return docRef.id;
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error('Failed to create class');
      throw error;
    }
  };

  const updateClass = async (id, updates) => {
    try {
      const classRef = doc(db, 'class_management', id);
      await updateDoc(classRef, {
        ...updates,
        updated_at: serverTimestamp(),
      });
      toast.success('Class updated successfully');
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Failed to update class');
      throw error;
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const classRef = doc(db, 'class_management', id);
      await updateDoc(classRef, {
        status,
        updated_at: serverTimestamp(),
      });
      // Don't show toast here as ActionButtons handles it
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  };

  const deleteClass = async (id) => {
    try {
      const classRef = doc(db, 'class_management', id);
      await deleteDoc(classRef);
      // Don't show toast here as ActionButtons handles it
    } catch (error) {
      console.error('Error deleting class:', error);
      throw error;
    }
  };

  const getClassesByStatus = (status) => {
    return classes.filter(classItem => classItem.status === status);
  };

  const getClassStats = () => {
    const stats = classes.reduce((acc, classItem) => {
      acc[classItem.status] = (acc[classItem.status] || 0) + 1;
      return acc;
    }, {});

    return {
      total: classes.length,
      pending: stats.pending || 0,
      'in-progress': stats['in-progress'] || 0,
      completed: stats.completed || 0,
      cancelled: stats.cancelled || 0,
    };
  };

  return {
    classes,
    loading,
    error,
    createClass,
    updateClass,
    updateStatus,
    deleteClass,
    getClassesByStatus,
    getClassStats,
  };
};